/**
 * Defensive helpers for parsing untrusted localStorage content (#105).
 *
 * localStorage is shared across the origin and writable by browser
 * extensions, console users, and any prior XSS. Always validate the
 * shape of parsed data before consuming it; discard malformed entries.
 */

export type Validator<T> = (value: unknown) => value is T

export function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function isArrayOf<T>(itemValidator: Validator<T>): Validator<T[]> {
  return ((value: unknown): value is T[] =>
    Array.isArray(value) && value.every(itemValidator)) as Validator<T[]>
}

/**
 * Read JSON from localStorage and run it through a validator. Returns
 * `fallback` if the key is absent, the JSON is malformed, or the parsed
 * value fails validation. Removes the key on validation failure so the
 * next read starts clean.
 */
export function readValidatedJSON<T>(
  key: string,
  validator: Validator<T>,
  fallback: T | null = null
): T | null {
  try {
    const raw = localStorage.getItem(key)
    if (raw === null) return fallback
    const parsed: unknown = JSON.parse(raw)
    if (!validator(parsed)) {
      localStorage.removeItem(key)
      return fallback
    }
    return parsed
  } catch {
    try {
      localStorage.removeItem(key)
    } catch { /* ignore quota / privacy mode errors */ }
    return fallback
  }
}
